// // screens/marketplace/OrderDetail.jsx
// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import {
//   ChevronLeft,
//   ChevronDown,
//   ChevronRight,
//   Package,
//   CheckCircle2,
//   Clock,
//   XCircle,
//   AlertCircle,
//   Truck,
//   Wallet,
//   CreditCard,
//   ShieldCheck,
//   ArrowRight,
//   Check,
//   History,
// } from "lucide-react";
// import { useAuth } from "../../providers/AuthProvider";
// import { useTheme } from "../../hooks/useTheme";
// import { useToast } from "../../providers/ToastProvider";
// import { variantKeyLabel } from "./pricingApi";

// /* ══════════════════════════════════════════════════════════
//    12-stage ladder — must match the backend enum.
//    ══════════════════════════════════════════════════════════ */

// const STATUS_FLOW = [
//   { value: "ORDER_CONFIRMED", label: "Order Confirmed" },
//   { value: "SHOPPING_IN_PROGRESS", label: "Shopping in Progress" },
//   { value: "PURCHASED", label: "Purchased" },
//   {
//     value: "IN_TRANSIT_TO_CHINA_WAREHOUSE",
//     label: "In Transit to China Warehouse",
//   },
//   {
//     value: "RECEIVED_AT_CHINA_WAREHOUSE",
//     label: "Received at China Warehouse",
//   },
//   { value: "INSPECTION_IN_PROGRESS", label: "Inspection in Progress" },
//   { value: "PREPARING_FOR_SHIPMENT", label: "Preparing for Shipment" },
//   { value: "SHIPPED_FROM_CHINA", label: "Shipped from China" },
//   { value: "ARRIVED_IN_GHANA", label: "Arrived in Ghana" },
//   { value: "READY_FOR_PICKUP", label: "Ready for Pickup" },
//   { value: "OUT_FOR_DELIVERY", label: "Out for Delivery" },
//   { value: "DELIVERED_SUCCESSFULLY", label: "Delivered Successfully" },
// ];

// const STATUS_INDEX = Object.fromEntries(
//   STATUS_FLOW.map((s, i) => [s.value, i])
// );
// const FEE_TRIGGER_INDEX = STATUS_INDEX.ARRIVED_IN_GHANA;

// const statusIndex = (value) => STATUS_INDEX[value] ?? -1;
// const statusLabel = (value) =>
//   STATUS_FLOW.find((s) => s.value === value)?.label || value || "—";

// const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

// const fmtDate = (iso) =>
//   iso
//     ? new Date(iso).toLocaleDateString(undefined, {
//         month: "short",
//         day: "numeric",
//         year: "numeric",
//       })
//     : "—";

// const readApiError = (data, fallback) => {
//   if (!data) return fallback;
//   if (data.message) return data.message;
//   const errs = data.errors;
//   if (errs) {
//     const parts = [];
//     for (const [k, v] of Object.entries(errs)) {
//       const arr = Array.isArray(v) ? v : [v];
//       arr.forEach((msg) => {
//         if (k === "non_field_errors" || k === "detail") parts.push(msg);
//         else parts.push(`${k.replace(/_/g, " ")}: ${msg}`);
//       });
//     }
//     if (parts.length) return parts.join(" ");
//   }
//   return fallback;
// };

// const PAYMENT_METHODS = [
//   {
//     value: "PAYSTACK",
//     icon: CreditCard,
//     title: "Card or Mobile Money",
//     subtitle: "Secured by Paystack",
//   },
//   {
//     value: "WALLET",
//     icon: Wallet,
//     title: "Azoemart Wallet",
//     subtitle: "Instant payment from balance",
//   },
// ];

// /* ══════════════════════════════════════════════════════════
//    Tone helpers
//    ══════════════════════════════════════════════════════════ */

// const paymentTone = (t, status) => {
//   const map = {
//     PENDING: { fg: t.warning, bg: t.warningBg, label: "Awaiting payment" },
//     COMPLETED: { fg: t.success, bg: t.successBg, label: "Paid" },
//     FAILED: { fg: t.error, bg: t.errorBg, label: "Payment failed" },
//     CANCELLED: { fg: t.muted, bg: t.subtle, label: "Cancelled" },
//   };
//   return map[status] || { fg: t.muted, bg: t.subtle, label: status || "—" };
// };

// const shippingPaymentTone = (t, status) => {
//   const map = {
//     PENDING: { fg: t.warning, bg: t.warningBg, label: "Shipping pending" },
//     PARTIAL: { fg: t.accent, bg: t.hoverBg, label: "Partially paid" },
//     COMPLETED: { fg: t.success, bg: t.successBg, label: "Shipping paid" },
//     FAILED: { fg: t.error, bg: t.errorBg, label: "Shipping failed" },
//   };
//   return map[status] || { fg: t.muted, bg: t.subtle, label: status || "—" };
// };

// /* ══════════════════════════════════════════════════════════
//    OrderDetail (buyer)
//    ══════════════════════════════════════════════════════════ */

// export default function OrderDetail() {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const { authFetch } = useAuth();
//   const { colors } = useTheme();
//   const { showToast } = useToast();

//   const [order, setOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedIds, setSelectedIds] = useState(() => new Set());
//   const [paymentMethod, setPaymentMethod] = useState("PAYSTACK");
//   const [paying, setPaying] = useState(false);
//   const [expandedItems, setExpandedItems] = useState(() => new Set());

//   const load = useCallback(async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const res = await authFetch(`/marketplace/orders/${id}/`);
//       const data = await res.json();
//       if (!res.ok) throw new Error(readApiError(data, "Could not load order"));
//       setOrder(data.data);
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   }, [authFetch, id]);

//   useEffect(() => {
//     load();
//   }, [load]);

//   const items = order?.items || [];
//   const paid = order?.payment_status === "COMPLETED";
//   const payment = order ? paymentTone(colors, order.payment_status) : null;

//   /* ── Shipping-eligible items ────────────────────────── */
//   const itemEligibility = useMemo(() => {
//     return items.map((item) => {
//       const idx = statusIndex(item.status);
//       const shippingPaid = item.shipping_payment_status === "COMPLETED";
//       const hasFee = Number(item.shipping_fee) > 0;
//       const arrived = idx >= FEE_TRIGGER_INDEX && idx >= 0;
//       const eligible = paid && arrived && !shippingPaid && hasFee;
//       return { item, eligible, shippingPaid, hasFee, arrived };
//     });
//   }, [items, paid]);

//   const eligibleItems = itemEligibility.filter((r) => r.eligible);

//   const payableTotal = useMemo(
//     () =>
//       [...selectedIds].reduce((sum, id) => {
//         const row = itemEligibility.find((r) => r.item.id === id);
//         if (!row) return sum;
//         const unit = Number(row.item.shipping_fee || 0);
//         const qty = Number(row.item.quantity || 1);
//         return sum + unit * qty;
//       }, 0),
//     [selectedIds, itemEligibility]
//   );

//   /* ── Pay shipping ───────────────────────────────────── */
//   const payShipping = async () => {
//     if (selectedIds.size === 0) return;
//     setPaying(true);
//     try {
//       const res = await authFetch(
//         `/marketplace/orders/${id}/pay-shipping/`,
//         {
//           method: "POST",
//           body: JSON.stringify({
//             order_item_ids: [...selectedIds],
//             payment_method: paymentMethod,
//           }),
//         }
//       );
//       const data = await res.json();
//       if (!res.ok)
//         throw new Error(readApiError(data, "Could not start shipping payment"));
//       if (paymentMethod === "PAYSTACK" && data.data?.authorization_url) {
//         window.location.assign(data.data.authorization_url);
//         return;
//       }
//       showToast("Shipping paid from your wallet", "success");
//       setSelectedIds(new Set());
//       await load();
//     } catch (err) {
//       showToast(err.message, "error");
//     } finally {
//       setPaying(false);
//     }
//   };

//   /* ── Pay product cost (unpaid orders) ───────────────── */
//   const payOrder = async () => {
//     setPaying(true);
//     try {
//       const res = await authFetch(`/marketplace/orders/${id}/pay/`, {
//         method: "POST",
//         body: JSON.stringify({ payment_method: paymentMethod }),
//       });
//       const data = await res.json();
//       if (!res.ok)
//         throw new Error(readApiError(data, "Could not start payment"));
//       if (paymentMethod === "PAYSTACK" && data.data?.authorization_url) {
//         window.location.assign(data.data.authorization_url);
//         return;
//       }
//       showToast("Order paid from your wallet", "success");
//       await load();
//     } catch (err) {
//       showToast(err.message, "error");
//     } finally {
//       setPaying(false);
//     }
//   };

//   /* ── Cancel unpaid order ────────────────────────────── */
//   const cancelOrder = async () => {
//     if (!window.confirm("Cancel this order? This cannot be undone.")) return;
//     setPaying(true);
//     try {
//       const res = await authFetch(`/marketplace/orders/${id}/cancel/`, {
//         method: "POST",
//       });
//       if (!res.ok) {
//         const d = await res.json().catch(() => ({}));
//         throw new Error(readApiError(d, "Could not cancel order"));
//       }
//       showToast("Order cancelled", "success");
//       await load();
//     } catch (err) {
//       showToast(err.message, "error");
//     } finally {
//       setPaying(false);
//     }
//   };

//   /* ── Selection ──────────────────────────────────────── */
//   const toggleItem = (itemId) =>
//     setExpandedItems((prev) => {
//       const next = new Set(prev);
//       if (next.has(itemId)) next.delete(itemId);
//       else next.add(itemId);
//       return next;
//     });

//   const toggleSelect = (itemId) =>
//     setSelectedIds((prev) => {
//       const next = new Set(prev);
//       if (next.has(itemId)) next.delete(itemId);
//       else next.add(itemId);
//       return next;
//     });

//   const toggleSelectAll = () => {
//     if (selectedIds.size === eligibleItems.length) setSelectedIds(new Set());
//     else setSelectedIds(new Set(eligibleItems.map((r) => r.item.id)));
//   };

//   /* ── Loading / error ────────────────────────────────── */
//   if (loading) {
//     return (
//       <div style={styles.pageWrap}>
//         <div style={styles.loadingWrap(colors)}>
//           <div style={styles.spinner(colors)} />
//           <span style={{ color: colors.secondaryText, fontSize: 13 }}>
//             Loading order…
//           </span>
//         </div>
//       </div>
//     );
//   }

//   if (error || !order) {
//     return (
//       <div style={styles.pageWrap}>
//         <button
//           type="button"
//           onClick={() => navigate("/marketplace/orders")}
//           style={styles.backBtn(colors)}
//         >
//           <ChevronLeft size={16} /> My orders
//         </button>
//         <div style={styles.errorWrap(colors)}>
//           <div style={styles.errorIcon(colors)}>
//             <AlertCircle size={24} />
//           </div>
//           <h2 style={styles.errorTitle(colors)}>Order not available</h2>
//           <p style={styles.errorText(colors)}>
//             {error || "This order may have been removed."}
//           </p>
//           <button
//             type="button"
//             onClick={() => navigate("/marketplace/orders")}
//             style={styles.primaryBtn(colors)}
//           >
//             Back to my orders
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const shippingTone = shippingPaymentTone(colors, order.shipping_payment_status);
//   const grandTotal = order.grand_total ?? 0;

//   return (
//     <div style={styles.pageWrap}>
//       <button
//         type="button"
//         onClick={() => navigate(-1)}
//         style={styles.backBtn(colors)}
//       >
//         <ChevronLeft size={16} /> Back
//       </button>

//       {/* ── Header ──────────────────────────────────────── */}
//       <header style={styles.header(colors)}>
//         <div style={styles.headerTop}>
//           <div style={{ minWidth: 0 }}>
//             <div style={styles.headerEyebrow(colors)}>
//               Order #{String(order.id).slice(0, 8)}
//             </div>
//             <div style={styles.headerTitle(colors)}>
//               {fmtDate(order.created_at)} · {items.length} item
//               {items.length === 1 ? "" : "s"}
//             </div>
//           </div>
//           <span
//             style={{
//               ...styles.headerStatus,
//               background: payment.bg,
//               color: payment.fg,
//             }}
//           >
//             {order.payment_status === "COMPLETED" ? (
//               <CheckCircle2 size={12} />
//             ) : order.payment_status === "FAILED" ? (
//               <XCircle size={12} />
//             ) : (
//               <Clock size={12} />
//             )}
//             {payment.label}
//           </span>
//         </div>

//         {paid && (
//           <div style={styles.headerMetrics}>
//             <Metric label="Subtotal" value={money(order.subtotal)} colors={colors} />
//             <Metric
//               label="Shipping"
//               value={
//                 order.shipping_payment_status === "COMPLETED"
//                   ? "Paid"
//                   : money(order.shipping_fee)
//               }
//               tone={
//                 order.shipping_payment_status === "COMPLETED"
//                   ? "success"
//                   : undefined
//               }
//               colors={colors}
//             />
//             <Metric
//               label="Total"
//               value={money(grandTotal)}
//               emphasis
//               colors={colors}
//             />
//           </div>
//         )}
//       </header>

//       {/* ══════════════════════════════════════════════════
//          UNPAID → only payment gate
//          ══════════════════════════════════════════════════ */}
//       {!paid ? (
//         <PaymentGate
//           colors={colors}
//           order={order}
//           paymentMethod={paymentMethod}
//           setPaymentMethod={setPaymentMethod}
//           onPay={payOrder}
//           onCancel={cancelOrder}
//           paying={paying}
//         />
//       ) : (
//         <>
//           {/* ── Shipping payment block ───────────────── */}
//           {eligibleItems.length > 0 && (
//             <ShippingPayBlock
//               colors={colors}
//               eligibleItems={eligibleItems}
//               selectedIds={selectedIds}
//               onToggle={toggleSelect}
//               onToggleAll={toggleSelectAll}
//               payableTotal={payableTotal}
//               paymentMethod={paymentMethod}
//               setPaymentMethod={setPaymentMethod}
//               onPay={payShipping}
//               paying={paying}
//             />
//           )}

//           {/* ── Shipping payment status banner ─────────── */}
//           {eligibleItems.length === 0 &&
//             itemEligibility.some((r) => r.shippingPaid) && (
//               <div style={styles.successBanner(colors)}>
//                 <CheckCircle2 size={16} />
//                 Shipping paid for all eligible items on this order.
//               </div>
//             )}

//           {/* ── Fulfilment overview ────────────────────── */}
//           <section style={styles.section(colors)}>
//             <div style={styles.sectionHeader}>
//               <h2 style={styles.sectionTitle(colors)}>Fulfilment</h2>
//               <span style={styles.sectionSubtitle(colors)}>
//                 {order.status_display || statusLabel(order.status)}
//               </span>
//             </div>
//             <OrderLadder order={order} colors={colors} />
//           </section>

//           {/* ── Items ──────────────────────────────────── */}
//           <section style={styles.section(colors)}>
//             <div style={styles.sectionHeader}>
//               <h2 style={styles.sectionTitle(colors)}>Items</h2>
//               <span style={styles.sectionSubtitle(colors)}>
//                 {items.length} item{items.length === 1 ? "" : "s"}
//               </span>
//             </div>

//             <div style={{ display: "grid", gap: 10 }}>
//               {itemEligibility.map(
//                 ({ item, eligible, shippingPaid, hasFee, arrived }) => (
//                   <BuyerItemCard
//                     key={item.id}
//                     item={item}
//                     colors={colors}
//                     eligible={eligible}
//                     shippingPaid={shippingPaid}
//                     hasFee={hasFee}
//                     arrived={arrived}
//                     selected={selectedIds.has(item.id)}
//                     expanded={expandedItems.has(item.id)}
//                     onToggleSelect={() => toggleSelect(item.id)}
//                     onToggleExpand={() => toggleItem(item.id)}
//                   />
//                 )
//               )}
//             </div>
//           </section>

//           {/* ── Shipping payment status ────────────────── */}
//           <div style={styles.shippingStatusRow(colors)}>
//             <span style={{ fontSize: 12, color: colors.muted }}>Shipping</span>
//             <span
//               style={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 gap: 5,
//                 fontSize: 11.5,
//                 fontWeight: 700,
//                 padding: "3px 10px",
//                 borderRadius: 999,
//                 background: shippingTone.bg,
//                 color: shippingTone.fg,
//               }}
//             >
//               {shippingTone.label}
//             </span>
//           </div>
//         </>
//       )}

//       <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
//     </div>
//   );
// }

// /* ══════════════════════════════════════════════════════════
//    Payment gate (unpaid order)
//    ══════════════════════════════════════════════════════════ */

// const PaymentGate = ({
//   colors,
//   order,
//   paymentMethod,
//   setPaymentMethod,
//   onPay,
//   onCancel,
//   paying,
// }) => {
//   const payment = paymentTone(colors, order.payment_status);
//   const isFailed = order.payment_status === "FAILED";
//   const isCancelled = order.payment_status === "CANCELLED";

//   return (
//     <div style={styles.gateCard(colors, payment.fg)}>
//       <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
//         <div
//           style={{
//             width: 48,
//             height: 48,
//             borderRadius: 14,
//             background: payment.bg,
//             color: payment.fg,
//             display: "inline-flex",
//             alignItems: "center",
//             justifyContent: "center",
//             flexShrink: 0,
//           }}
//         >
//           {isFailed ? (
//             <XCircle size={24} />
//           ) : isCancelled ? (
//             <XCircle size={24} />
//           ) : (
//             <Clock size={24} />
//           )}
//         </div>
//         <div style={{ flex: 1, minWidth: 0 }}>
//           <div style={{ fontSize: 16, fontWeight: 800, color: colors.heading }}>
//             {payment.label}
//           </div>
//           <p
//             style={{
//               margin: "4px 0 0",
//               fontSize: 13,
//               color: colors.muted,
//               lineHeight: 1.55,
//             }}
//           >
//             {isFailed
//               ? "Your payment didn't go through. Try again or cancel the order."
//               : isCancelled
//               ? "This order has been cancelled."
//               : "Complete payment to start fulfilment. Nothing ships until payment is confirmed."}
//           </p>
//         </div>
//       </div>

//       <div
//         style={{
//           marginTop: 16,
//           paddingTop: 16,
//           borderTop: `1px solid ${colors.separator || colors.border}`,
//           display: "flex",
//           justifyContent: "space-between",
//           alignItems: "baseline",
//         }}
//       >
//         <span style={{ fontSize: 12.5, color: colors.muted, fontWeight: 600 }}>
//           Order total
//         </span>
//         <span
//           style={{
//             fontSize: 22,
//             fontWeight: 800,
//             color: colors.heading,
//             letterSpacing: "-0.02em",
//           }}
//         >
//           {money(order.total)}
//         </span>
//       </div>

//       {!isCancelled && (
//         <>
//           <div
//             style={{
//               marginTop: 16,
//               display: "grid",
//               gap: 8,
//             }}
//           >
//             {PAYMENT_METHODS.map((m) => {
//               const Icon = m.icon;
//               const active = paymentMethod === m.value;
//               return (
//                 <button
//                   key={m.value}
//                   type="button"
//                   onClick={() => setPaymentMethod(m.value)}
//                   disabled={paying}
//                   style={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: 12,
//                     width: "100%",
//                     padding: 12,
//                     borderRadius: 12,
//                     border: `1.5px solid ${
//                       active ? colors.accent : colors.border
//                     }`,
//                     background: active ? colors.hoverBg : "transparent",
//                     cursor: paying ? "not-allowed" : "pointer",
//                     fontFamily: "inherit",
//                     textAlign: "left",
//                   }}
//                 >
//                   <span
//                     style={{
//                       width: 30,
//                       height: 30,
//                       borderRadius: 9,
//                       background: active ? colors.accent : colors.subtle,
//                       color: active ? colors.accentText : colors.muted,
//                       display: "inline-flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                       flexShrink: 0,
//                     }}
//                   >
//                     <Icon size={15} />
//                   </span>
//                   <span style={{ flex: 1 }}>
//                     <span
//                       style={{
//                         display: "block",
//                         fontSize: 13,
//                         fontWeight: 700,
//                         color: colors.heading,
//                       }}
//                     >
//                       {m.title}
//                     </span>
//                     <span
//                       style={{
//                         display: "block",
//                         fontSize: 11.5,
//                         color: colors.muted,
//                         marginTop: 1,
//                       }}
//                     >
//                       {m.subtitle}
//                     </span>
//                   </span>
//                   <span
//                     style={{
//                       width: 16,
//                       height: 16,
//                       borderRadius: "50%",
//                       border: `1.5px solid ${
//                         active ? colors.accent : colors.border
//                       }`,
//                       display: "inline-flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                     }}
//                   >
//                     {active && (
//                       <span
//                         style={{
//                           width: 7,
//                           height: 7,
//                           borderRadius: "50%",
//                           background: colors.accent,
//                         }}
//                       />
//                     )}
//                   </span>
//                 </button>
//               );
//             })}
//           </div>

//           <div
//             style={{
//               marginTop: 16,
//               display: "grid",
//               gridTemplateColumns: "1fr auto",
//               gap: 10,
//             }}
//           >
//             <button
//               type="button"
//               onClick={onPay}
//               disabled={paying}
//               style={{
//                 padding: "13px 18px",
//                 borderRadius: 12,
//                 border: "none",
//                 background: paying ? colors.subtle : colors.accent,
//                 color: paying ? colors.secondaryText : colors.accentText,
//                 fontSize: 14.5,
//                 fontWeight: 700,
//                 cursor: paying ? "not-allowed" : "pointer",
//                 display: "inline-flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 gap: 8,
//                 boxShadow: paying ? "none" : `0 4px 14px ${colors.accent}33`,
//                 fontFamily: "inherit",
//               }}
//             >
//               {paying ? (
//                 <>
//                   <span
//                     style={{
//                       width: 14,
//                       height: 14,
//                       border: `2px solid ${colors.accentText}55`,
//                       borderTopColor: colors.accentText,
//                       borderRadius: "50%",
//                       animation: "spin 0.8s linear infinite",
//                     }}
//                   />
//                   Processing…
//                 </>
//               ) : (
//                 <>
//                   Complete payment · {money(order.total)}
//                   <ArrowRight size={15} />
//                 </>
//               )}
//             </button>
//             <button
//               type="button"
//               onClick={onCancel}
//               disabled={paying}
//               style={{
//                 padding: "13px 18px",
//                 borderRadius: 12,
//                 border: `1px solid ${colors.border}`,
//                 background: "transparent",
//                 color: colors.text,
//                 fontSize: 13.5,
//                 fontWeight: 600,
//                 cursor: paying ? "not-allowed" : "pointer",
//                 fontFamily: "inherit",
//               }}
//             >
//               Cancel
//             </button>
//           </div>

//           <div
//             style={{
//               marginTop: 12,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               gap: 6,
//               fontSize: 11.5,
//               color: colors.muted,
//             }}
//           >
//             <ShieldCheck size={12} />
//             Secure checkout · Your details are encrypted
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Order ladder (compact rollup)
//    ══════════════════════════════════════════════════════════ */

// const OrderLadder = ({ order, colors }) => {
//   const currentIdx = statusIndex(order.status);
//   const isTerminal =
//     order.status === "FAILED" || order.status === "CANCELLED";
//   const pct = isTerminal
//     ? 0
//     : currentIdx >= 0
//     ? ((currentIdx + 1) / STATUS_FLOW.length) * 100
//     : 0;
//   const next = STATUS_FLOW[currentIdx + 1];

//   return (
//     <div>
//       <div
//         style={{
//           display: "flex",
//           alignItems: "center",
//           gap: 8,
//           fontSize: 12,
//           color: colors.muted,
//           marginBottom: 8,
//         }}
//       >
//         <span
//           style={{
//             fontSize: 12,
//             fontWeight: 800,
//             letterSpacing: 0.2,
//             color: isTerminal ? colors.error : colors.text,
//           }}
//         >
//           {order.status_display || statusLabel(order.status)}
//         </span>
//         {!isTerminal && currentIdx >= 0 && (
//           <span style={{ color: colors.muted }}>
//             · step {currentIdx + 1} of {STATUS_FLOW.length}
//           </span>
//         )}
//       </div>
//       <div
//         style={{
//           height: 4,
//           borderRadius: 2,
//           background: colors.hoverBg,
//           overflow: "hidden",
//         }}
//       >
//         <div
//           style={{
//             width: `${pct}%`,
//             height: "100%",
//             background: isTerminal ? colors.error : colors.accent,
//             transition: "width 0.3s ease",
//           }}
//         />
//       </div>
//       {!isTerminal && next && (
//         <div
//           style={{
//             marginTop: 6,
//             fontSize: 11.5,
//             color: colors.muted,
//           }}
//         >
//           Next: {next.label}
//         </div>
//       )}
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Shipping payment block
//    ══════════════════════════════════════════════════════════ */

// const ShippingPayBlock = ({
//   colors,
//   eligibleItems,
//   selectedIds,
//   onToggle,
//   onToggleAll,
//   payableTotal,
//   paymentMethod,
//   setPaymentMethod,
//   onPay,
//   paying,
// }) => {
//   const allSelected = selectedIds.size === eligibleItems.length;
//   const someSelected = selectedIds.size > 0;

//   return (
//     <section
//       style={{
//         borderRadius: 16,
//         border: `1px solid ${colors.warning}55`,
//         background: colors.raisedBg,
//         padding: 18,
//         marginBottom: 16,
//       }}
//     >
//       <div
//         style={{
//           display: "flex",
//           alignItems: "flex-start",
//           justifyContent: "space-between",
//           gap: 12,
//           marginBottom: 14,
//         }}
//       >
//         <div style={{ minWidth: 0 }}>
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: 8,
//               fontSize: 15,
//               fontWeight: 800,
//               color: colors.heading,
//               letterSpacing: "-0.01em",
//             }}
//           >
//             <Truck size={16} color={colors.warning} />
//             Shipping fees ready to pay
//           </div>
//           <p
//             style={{
//               margin: "4px 0 0",
//               fontSize: 12.5,
//               color: colors.muted,
//               lineHeight: 1.5,
//             }}
//           >
//             These items have arrived in Ghana. Pay shipping to have them
//             released for pickup or delivery.
//           </p>
//         </div>
//         {eligibleItems.length > 1 && (
//           <button
//             type="button"
//             onClick={onToggleAll}
//             style={{
//               background: "transparent",
//               border: "none",
//               cursor: "pointer",
//               color: colors.accent,
//               fontSize: 12.5,
//               fontWeight: 700,
//               padding: "4px 8px",
//               borderRadius: 8,
//               flexShrink: 0,
//               fontFamily: "inherit",
//             }}
//           >
//             {allSelected ? "Clear" : "Select all"}
//           </button>
//         )}
//       </div>

//       <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
//         {eligibleItems.map(({ item }) => {
//           const selected = selectedIds.has(item.id);
//           const unit = Number(item.shipping_fee || 0);
//           const qty = Number(item.quantity || 1);
//           const lineTotal = unit * qty;
//           return (
//             <div
//               key={item.id}
//               onClick={() => onToggle(item.id)}
//               style={{
//                 display: "flex",
//                 alignItems: "center",
//                 gap: 10,
//                 padding: "10px 12px",
//                 borderRadius: 10,
//                 border: `1px solid ${
//                   selected ? colors.accent : colors.border
//                 }`,
//                 background: selected ? `${colors.accent}0A` : colors.raisedBg,
//                 cursor: "pointer",
//                 transition: "background 0.12s ease, border-color 0.12s ease",
//               }}
//             >
//               <div
//                 style={{
//                   width: 18,
//                   height: 18,
//                   borderRadius: 5,
//                   border: `1.5px solid ${
//                     selected ? colors.accent : colors.border
//                   }`,
//                   background: selected ? colors.accent : "transparent",
//                   display: "inline-flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   flexShrink: 0,
//                 }}
//               >
//                 {selected && <Check size={11} color={colors.accentText} />}
//               </div>
//               <div style={{ flex: 1, minWidth: 0 }}>
//                 <div
//                   style={{
//                     fontSize: 13,
//                     fontWeight: 700,
//                     color: colors.heading,
//                     whiteSpace: "nowrap",
//                     overflow: "hidden",
//                     textOverflow: "ellipsis",
//                   }}
//                 >
//                   {item.product_name_display || item.product_name} ×{qty}
//                 </div>
//                 <div
//                   style={{
//                     fontSize: 11.5,
//                     color: colors.muted,
//                     marginTop: 2,
//                   }}
//                 >
//                   {money(unit)}/unit · {money(lineTotal)}
//                 </div>
//               </div>
//             </div>
//           );
//         })}
//       </div>

//       <div style={{ display: "grid", gap: 10 }}>
//         <div style={{ display: "grid", gap: 8 }}>
//           {PAYMENT_METHODS.map((m) => {
//             const Icon = m.icon;
//             const active = paymentMethod === m.value;
//             return (
//               <button
//                 key={m.value}
//                 type="button"
//                 onClick={() => setPaymentMethod(m.value)}
//                 disabled={paying}
//                 style={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: 12,
//                   width: "100%",
//                   padding: 10,
//                   borderRadius: 10,
//                   border: `1.5px solid ${
//                     active ? colors.accent : colors.border
//                   }`,
//                   background: active ? colors.hoverBg : "transparent",
//                   cursor: paying ? "not-allowed" : "pointer",
//                   fontFamily: "inherit",
//                   textAlign: "left",
//                 }}
//               >
//                 <span
//                   style={{
//                     width: 28,
//                     height: 28,
//                     borderRadius: 8,
//                     background: active ? colors.accent : colors.subtle,
//                     color: active ? colors.accentText : colors.muted,
//                     display: "inline-flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     flexShrink: 0,
//                   }}
//                 >
//                   <Icon size={14} />
//                 </span>
//                 <span style={{ flex: 1 }}>
//                   <span
//                     style={{
//                       display: "block",
//                       fontSize: 12.5,
//                       fontWeight: 700,
//                       color: colors.heading,
//                     }}
//                   >
//                     {m.title}
//                   </span>
//                   <span
//                     style={{
//                       display: "block",
//                       fontSize: 11,
//                       color: colors.muted,
//                       marginTop: 1,
//                     }}
//                   >
//                     {m.subtitle}
//                   </span>
//                 </span>
//                 <span
//                   style={{
//                     width: 16,
//                     height: 16,
//                     borderRadius: "50%",
//                     border: `1.5px solid ${
//                       active ? colors.accent : colors.border
//                     }`,
//                     display: "inline-flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     flexShrink: 0,
//                   }}
//                 >
//                   {active && (
//                     <span
//                       style={{
//                         width: 7,
//                         height: 7,
//                         borderRadius: "50%",
//                         background: colors.accent,
//                       }}
//                     />
//                   )}
//                 </span>
//               </button>
//             );
//           })}
//         </div>

//         <button
//           type="button"
//           disabled={!someSelected || paying}
//           onClick={onPay}
//           style={{
//             width: "100%",
//             padding: "13px 18px",
//             borderRadius: 12,
//             border: "none",
//             background:
//               someSelected && !paying
//                 ? colors.accent
//                 : colors.subtle || colors.hoverBg,
//             color:
//               someSelected && !paying
//                 ? colors.accentText
//                 : colors.secondaryText,
//             fontSize: 14.5,
//             fontWeight: 700,
//             cursor: someSelected && !paying ? "pointer" : "not-allowed",
//             display: "inline-flex",
//             alignItems: "center",
//             justifyContent: "center",
//             gap: 8,
//             boxShadow:
//               someSelected && !paying
//                 ? `0 4px 14px ${colors.accent}33`
//                 : "none",
//             fontFamily: "inherit",
//           }}
//         >
//           {paying ? (
//             <>
//               <span
//                 style={{
//                   width: 14,
//                   height: 14,
//                   border: `2px solid ${colors.accentText}55`,
//                   borderTopColor: colors.accentText,
//                   borderRadius: "50%",
//                   animation: "spin 0.8s linear infinite",
//                 }}
//               />
//               Processing…
//             </>
//           ) : (
//             <>
//               Pay shipping · {money(payableTotal)}
//               <ArrowRight size={15} />
//             </>
//           )}
//         </button>

//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             gap: 6,
//             fontSize: 11.5,
//             color: colors.muted,
//           }}
//         >
//           <ShieldCheck size={12} />
//           Secure payment · Fees unlock release of your items
//         </div>
//       </div>
//     </section>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Buyer item card
//    ══════════════════════════════════════════════════════════ */

// const BuyerItemCard = ({
//   item,
//   colors,
//   eligible,
//   shippingPaid,
//   hasFee,
//   arrived,
//   selected,
//   expanded,
//   onToggleSelect,
//   onToggleExpand,
// }) => {
//   const currentIdx = statusIndex(item.status);
//   const next = STATUS_FLOW[currentIdx + 1];
//   const pct =
//     currentIdx >= 0 ? ((currentIdx + 1) / STATUS_FLOW.length) * 100 : 0;

//   return (
//     <div
//       style={{
//         borderRadius: 12,
//         border: `1px solid ${selected ? colors.accent : colors.border}`,
//         background: selected ? `${colors.accent}0A` : colors.raisedBg,
//         overflow: "hidden",
//       }}
//     >
//       <div style={{ display: "flex", gap: 12, padding: 12 }}>
//         {eligible && (
//           <button
//             type="button"
//             onClick={onToggleSelect}
//             style={{
//               width: 22,
//               height: 22,
//               borderRadius: 6,
//               border: `1.5px solid ${
//                 selected ? colors.accent : colors.border
//               }`,
//               background: selected ? colors.accent : "transparent",
//               display: "inline-flex",
//               alignItems: "center",
//               justifyContent: "center",
//               cursor: "pointer",
//               flexShrink: 0,
//               marginTop: 4,
//             }}
//           >
//             {selected && <Check size={12} color={colors.accentText} />}
//           </button>
//         )}
//         <div
//           style={{
//             width: 56,
//             height: 56,
//             borderRadius: 10,
//             overflow: "hidden",
//             background: colors.subtle,
//             flexShrink: 0,
//             display: "inline-flex",
//             alignItems: "center",
//             justifyContent: "center",
//           }}
//         >
//           {item.product_image ? (
//             <img
//               src={item.product_image}
//               alt=""
//               style={{ width: "100%", height: "100%", objectFit: "cover" }}
//             />
//           ) : (
//             <Package size={20} color={colors.muted} />
//           )}
//         </div>
//         <div style={{ flex: 1, minWidth: 0 }}>
//           <div
//             style={{
//               display: "flex",
//               alignItems: "flex-start",
//               justifyContent: "space-between",
//               gap: 8,
//             }}
//           >
//             <div style={{ minWidth: 0 }}>
//               <div
//                 style={{
//                   fontSize: 14,
//                   fontWeight: 700,
//                   color: colors.heading,
//                   lineHeight: 1.35,
//                 }}
//               >
//                 {item.product_name_display || item.product_name}
//               </div>
//               {item.variant_key && (
//                 <div
//                   style={{
//                     display: "inline-flex",
//                     alignItems: "center",
//                     marginTop: 4,
//                     padding: "2px 8px",
//                     borderRadius: 999,
//                     background: `${colors.accent}18`,
//                     color: colors.accent,
//                     fontSize: 10.5,
//                     fontWeight: 700,
//                   }}
//                 >
//                   {variantKeyLabel(item.variant_key)}
//                 </div>
//               )}
//               <div
//                 style={{
//                   fontSize: 12,
//                   color: colors.muted,
//                   marginTop: 4,
//                 }}
//               >
//                 Qty {item.quantity} · {money(item.price)} each ·{" "}
//                 {money(item.total)}
//               </div>
//             </div>
//             <button
//               type="button"
//               onClick={onToggleExpand}
//               aria-label={expanded ? "Collapse" : "Expand"}
//               style={{
//                 width: 28,
//                 height: 28,
//                 borderRadius: 6,
//                 border: `1px solid ${colors.border}`,
//                 background: "transparent",
//                 color: colors.muted,
//                 cursor: "pointer",
//                 display: "inline-flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 flexShrink: 0,
//               }}
//             >
//               {expanded ? (
//                 <ChevronDown size={14} />
//               ) : (
//                 <ChevronRight size={14} />
//               )}
//             </button>
//           </div>

//           {/* Compact stepper */}
//           <div style={{ marginTop: 10 }}>
//             <div
//               style={{
//                 fontSize: 11,
//                 fontWeight: 800,
//                 letterSpacing: 0.3,
//                 textTransform: "uppercase",
//                 color:
//                   currentIdx >= STATUS_FLOW.length - 1
//                     ? colors.success
//                     : colors.text,
//               }}
//             >
//               {item.status_display || statusLabel(item.status)}
//               {currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 && (
//                 <span
//                   style={{
//                     marginLeft: 6,
//                     fontWeight: 600,
//                     color: colors.muted,
//                     textTransform: "none",
//                     letterSpacing: 0,
//                   }}
//                 >
//                   · {currentIdx + 1}/{STATUS_FLOW.length}
//                 </span>
//               )}
//             </div>
//             <div
//               style={{
//                 marginTop: 5,
//                 height: 3,
//                 borderRadius: 2,
//                 background: colors.hoverBg,
//                 overflow: "hidden",
//               }}
//             >
//               <div
//                 style={{
//                   width: `${pct}%`,
//                   height: "100%",
//                   background: colors.accent,
//                   transition: "width 0.3s ease",
//                 }}
//               />
//             </div>
//             {next && (
//               <div
//                 style={{
//                   marginTop: 4,
//                   fontSize: 11,
//                   color: colors.muted,
//                 }}
//               >
//                 Next: {next.label}
//               </div>
//             )}
//           </div>

//           {/* Shipping row */}
//           <div
//             style={{
//               marginTop: 10,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               gap: 8,
//               flexWrap: "wrap",
//               paddingTop: 8,
//               borderTop: `1px solid ${colors.separator || colors.border}`,
//             }}
//           >
//             <span
//               style={{
//                 fontSize: 12,
//                 color: colors.muted,
//                 fontWeight: 600,
//               }}
//             >
//               {hasFee
//                 ? `Shipping ${money(item.shipping_fee)}/unit · ${money(
//                     item.shipping_total
//                   )}`
//                 : arrived
//                 ? "Shipping fee pending"
//                 : "Shipping not yet set"}
//             </span>
//             {shippingPaid && (
//               <span
//                 style={{
//                   display: "inline-flex",
//                   alignItems: "center",
//                   gap: 4,
//                   fontSize: 11.5,
//                   fontWeight: 700,
//                   color: colors.success,
//                 }}
//               >
//                 <CheckCircle2 size={12} />
//                 Shipping paid
//               </span>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Expanded detail */}
//       {expanded && (
//         <div
//           style={{
//             padding: "0 16px 14px",
//             borderTop: `1px solid ${colors.separator || colors.border}`,
//             paddingTop: 12,
//           }}
//         >
//           <div
//             style={{
//               fontSize: 11,
//               fontWeight: 800,
//               letterSpacing: 0.4,
//               textTransform: "uppercase",
//               color: colors.muted,
//               marginBottom: 8,
//             }}
//           >
//             Progress
//           </div>
//           <ol
//             style={{
//               margin: 0,
//               padding: 0,
//               listStyle: "none",
//               display: "grid",
//               gap: 2,
//             }}
//           >
//             {STATUS_FLOW.map((s, i) => {
//               const passed = i < currentIdx;
//               const current = i === currentIdx;
//               return (
//                 <li
//                   key={s.value}
//                   style={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: 8,
//                     padding: "4px 0",
//                   }}
//                 >
//                   <span
//                     style={{
//                       width: 14,
//                       height: 14,
//                       borderRadius: 7,
//                       border: `1.5px solid ${
//                         current ? colors.accent : passed ? colors.accent : colors.border
//                       }`,
//                       background:
//                         passed || current ? colors.accent : "transparent",
//                       display: "inline-flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                       flexShrink: 0,
//                     }}
//                   >
//                     {passed && <Check size={9} color={colors.accentText} />}
//                     {current && (
//                       <span
//                         style={{
//                           width: 5,
//                           height: 5,
//                           borderRadius: 3,
//                           background: colors.accentText,
//                         }}
//                       />
//                     )}
//                   </span>
//                   <span
//                     style={{
//                       fontSize: 12.5,
//                       fontWeight: current ? 800 : passed ? 600 : 500,
//                       color: current
//                         ? colors.heading
//                         : passed
//                         ? colors.text
//                         : colors.muted,
//                     }}
//                   >
//                     {s.label}
//                   </span>
//                 </li>
//               );
//             })}
//           </ol>
//         </div>
//       )}
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Metric
//    ══════════════════════════════════════════════════════════ */

// const Metric = ({ label, value, tone, emphasis, colors }) => {
//   const color =
//     tone === "success"
//       ? colors.success
//       : emphasis
//       ? colors.heading
//       : colors.text;
//   return (
//     <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
//       <span
//         style={{
//           fontSize: 10.5,
//           fontWeight: 700,
//           letterSpacing: 0.3,
//           textTransform: "uppercase",
//           color: colors.secondaryText,
//         }}
//       >
//         {label}
//       </span>
//       <span
//         style={{
//           fontSize: emphasis ? 18 : 14,
//           fontWeight: emphasis ? 800 : 700,
//           color,
//           letterSpacing: emphasis ? "-0.01em" : 0,
//         }}
//       >
//         {value}
//       </span>
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Styles
//    ══════════════════════════════════════════════════════════ */

// const styles = {
//   pageWrap: {
//     maxWidth: 900,
//     margin: "0 auto",
//     padding: "16px 16px 60px",
//   },
//   backBtn: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 4,
//     background: "transparent",
//     border: "none",
//     color: c.secondaryText,
//     cursor: "pointer",
//     padding: "6px 0",
//     marginBottom: 14,
//     fontSize: 13,
//     fontWeight: 600,
//   }),

//   header: (c) => ({
//     background: c.raisedBg,
//     border: `1px solid ${c.border}`,
//     borderRadius: 16,
//     padding: 20,
//     marginBottom: 16,
//   }),
//   headerTop: {
//     display: "flex",
//     alignItems: "flex-start",
//     justifyContent: "space-between",
//     gap: 12,
//     flexWrap: "wrap",
//     marginBottom: 16,
//   },
//   headerEyebrow: (c) => ({
//     fontSize: 11,
//     fontWeight: 800,
//     letterSpacing: 0.4,
//     textTransform: "uppercase",
//     color: c.muted,
//   }),
//   headerTitle: (c) => ({
//     marginTop: 4,
//     fontSize: 15,
//     fontWeight: 700,
//     color: c.heading,
//     letterSpacing: "-0.01em",
//   }),
//   headerStatus: {
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 5,
//     padding: "5px 12px",
//     borderRadius: 999,
//     fontSize: 11,
//     fontWeight: 800,
//     letterSpacing: 0.3,
//     textTransform: "uppercase",
//     flexShrink: 0,
//   },
//   headerMetrics: {
//     display: "grid",
//     gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
//     gap: 10,
//     paddingTop: 14,
//     borderTop: "1px solid transparent",
//   },

//   gateCard: (c, fg) => ({
//     padding: 20,
//     borderRadius: 16,
//     border: `1px solid ${fg}55`,
//     background: c.raisedBg,
//     marginBottom: 16,
//   }),

//   successBanner: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     padding: "12px 14px",
//     borderRadius: 12,
//     background: c.successBg,
//     color: c.success,
//     fontSize: 13,
//     fontWeight: 600,
//     marginBottom: 16,
//   }),

//   section: (c) => ({
//     background: c.raisedBg,
//     border: `1px solid ${c.border}`,
//     borderRadius: 16,
//     padding: 16,
//     marginBottom: 16,
//   }),
//   sectionHeader: {
//     display: "flex",
//     alignItems: "baseline",
//     justifyContent: "space-between",
//     gap: 8,
//     marginBottom: 12,
//     flexWrap: "wrap",
//   },
//   sectionTitle: (c) => ({
//     margin: 0,
//     fontSize: 15,
//     fontWeight: 800,
//     color: c.heading,
//     letterSpacing: "-0.01em",
//   }),
//   sectionSubtitle: (c) => ({
//     fontSize: 12.5,
//     color: c.muted,
//     fontWeight: 600,
//   }),

//   shippingStatusRow: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 8,
//     padding: "12px 14px",
//     borderRadius: 12,
//     background: c.subtleBg || c.hoverBg,
//     border: `1px solid ${c.border}`,
//     marginTop: 8,
//   }),

//   loadingWrap: (c) => ({
//     display: "flex",
//     flexDirection: "column",
//     alignItems: "center",
//     justifyContent: "center",
//     minHeight: 320,
//     gap: 12,
//   }),
//   spinner: (c) => ({
//     width: 32,
//     height: 32,
//     border: `3px solid ${c.border}`,
//     borderTopColor: c.accent,
//     borderRadius: "50%",
//     animation: "spin 0.9s linear infinite",
//   }),
//   errorWrap: (c) => ({
//     maxWidth: 380,
//     margin: "60px auto",
//     textAlign: "center",
//     padding: 24,
//   }),
//   errorIcon: (c) => ({
//     width: 64,
//     height: 64,
//     borderRadius: 20,
//     background: c.hoverBg,
//     color: c.secondaryText,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     marginBottom: 16,
//   }),
//   errorTitle: (c) => ({
//     margin: "0 0 6px",
//     fontSize: 18,
//     fontWeight: 800,
//     color: c.heading,
//   }),
//   errorText: (c) => ({
//     margin: "0 0 22px",
//     fontSize: 13.5,
//     color: c.secondaryText,
//     lineHeight: 1.55,
//   }),
//   primaryBtn: (c) => ({
//     padding: "11px 20px",
//     borderRadius: 10,
//     border: "none",
//     background: c.accent,
//     color: c.accentText,
//     fontWeight: 700,
//     fontSize: 13.5,
//     cursor: "pointer",
//   }),
// };






































// screens/marketplace/OrderDetail.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Package,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Truck,
  Wallet,
  CreditCard,
  ShieldCheck,
  ArrowRight,
  Check,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import { variantKeyLabel } from "./pricingApi";

/* ══════════════════════════════════════════════════════════
   12-stage ladder
   ══════════════════════════════════════════════════════════ */

const STATUS_FLOW = [
  { value: "ORDER_CONFIRMED", label: "Order Confirmed" },
  { value: "SHOPPING_IN_PROGRESS", label: "Shopping in Progress" },
  { value: "PURCHASED", label: "Purchased" },
  {
    value: "IN_TRANSIT_TO_CHINA_WAREHOUSE",
    label: "In Transit to China Warehouse",
  },
  {
    value: "RECEIVED_AT_CHINA_WAREHOUSE",
    label: "Received at China Warehouse",
  },
  { value: "INSPECTION_IN_PROGRESS", label: "Inspection in Progress" },
  { value: "PREPARING_FOR_SHIPMENT", label: "Preparing for Shipment" },
  { value: "SHIPPED_FROM_CHINA", label: "Shipped from China" },
  { value: "ARRIVED_IN_GHANA", label: "Arrived in Ghana" },
  { value: "READY_FOR_PICKUP", label: "Ready for Pickup" },
  { value: "OUT_FOR_DELIVERY", label: "Out for Delivery" },
  { value: "DELIVERED_SUCCESSFULLY", label: "Delivered Successfully" },
];

const STATUS_INDEX = Object.fromEntries(
  STATUS_FLOW.map((s, i) => [s.value, i])
);
const FEE_TRIGGER_INDEX = STATUS_INDEX.ARRIVED_IN_GHANA;

const statusIndex = (value) => STATUS_INDEX[value] ?? -1;
const statusLabel = (value) =>
  STATUS_FLOW.find((s) => s.value === value)?.label || value || "—";

const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const readApiError = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  const errs = data.errors;
  if (errs) {
    const parts = [];
    for (const [k, v] of Object.entries(errs)) {
      const arr = Array.isArray(v) ? v : [v];
      arr.forEach((msg) => {
        if (k === "non_field_errors" || k === "detail") parts.push(msg);
        else parts.push(`${k.replace(/_/g, " ")}: ${msg}`);
      });
    }
    if (parts.length) return parts.join(" ");
  }
  return fallback;
};

const PAYMENT_METHODS = [
  {
    value: "PAYSTACK",
    icon: CreditCard,
    title: "Card or Mobile Money",
    subtitle: "Secured by Paystack",
  },
  {
    value: "WALLET",
    icon: Wallet,
    title: "Azoemart Wallet",
    subtitle: "Instant payment from balance",
  },
];

const paymentTone = (c, status) => {
  const map = {
    PENDING: { fg: c.warning, bg: c.warningBg, label: "Awaiting payment" },
    COMPLETED: { fg: c.success, bg: c.successBg, label: "Paid" },
    FAILED: { fg: c.error, bg: c.errorBg, label: "Payment failed" },
    CANCELLED: { fg: c.secondaryText, bg: c.subtleBg, label: "Cancelled" },
  };
  return map[status] || { fg: c.secondaryText, bg: c.subtleBg, label: status || "—" };
};

const shippingPaymentTone = (c, status) => {
  const map = {
    PENDING: { fg: c.warning, bg: c.warningBg, label: "Shipping pending" },
    PARTIAL: { fg: c.linkColor, bg: c.hoverBg, label: "Partially paid" },
    COMPLETED: { fg: c.success, bg: c.successBg, label: "Shipping paid" },
    FAILED: { fg: c.error, bg: c.errorBg, label: "Shipping failed" },
  };
  return map[status] || { fg: c.secondaryText, bg: c.subtleBg, label: status || "—" };
};

/* ══════════════════════════════════════════════════════════
   OrderDetail (buyer)
   ══════════════════════════════════════════════════════════ */

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [paymentMethod, setPaymentMethod] = useState("PAYSTACK");
  const [paying, setPaying] = useState(false);
  const [expandedItems, setExpandedItems] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/marketplace/orders/${id}/`);
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Could not load order"));
      setOrder(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, id]);

  useEffect(() => {
    load();
  }, [load]);

  const items = order?.items || [];
  const paid = order?.payment_status === "COMPLETED";
  const payment = order ? paymentTone(colors, order.payment_status) : null;

  const itemEligibility = useMemo(() => {
    return items.map((item) => {
      const idx = statusIndex(item.status);
      const shippingPaid = item.shipping_payment_status === "COMPLETED";
      const hasFee = Number(item.shipping_fee) > 0;
      const arrived = idx >= FEE_TRIGGER_INDEX && idx >= 0;
      const eligible = paid && arrived && !shippingPaid && hasFee;
      return { item, eligible, shippingPaid, hasFee, arrived };
    });
  }, [items, paid]);

  const eligibleItems = useMemo(
    () => itemEligibility.filter((r) => r.eligible),
    [itemEligibility]
  );

  const payableTotal = useMemo(
    () =>
      [...selectedIds].reduce((sum, sid) => {
        const row = itemEligibility.find((r) => r.item.id === sid);
        if (!row) return sum;
        const unit = Number(row.item.shipping_fee || 0);
        const qty = Number(row.item.quantity || 1);
        return sum + unit * qty;
      }, 0),
    [selectedIds, itemEligibility]
  );

  const payShipping = async () => {
    if (selectedIds.size === 0) return;
    setPaying(true);
    try {
      const res = await authFetch(
        `/marketplace/orders/${id}/pay-shipping/`,
        {
          method: "POST",
          body: JSON.stringify({
            order_item_ids: [...selectedIds],
            payment_method: paymentMethod,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok)
        throw new Error(readApiError(data, "Could not start shipping payment"));
      if (paymentMethod === "PAYSTACK" && data.data?.authorization_url) {
        window.location.assign(data.data.authorization_url);
        return;
      }
      showToast("Shipping paid from your wallet", "success");
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setPaying(false);
    }
  };

  const payOrder = async () => {
    setPaying(true);
    try {
      const res = await authFetch(`/marketplace/orders/${id}/pay/`, {
        method: "POST",
        body: JSON.stringify({ payment_method: paymentMethod }),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(readApiError(data, "Could not start payment"));
      if (paymentMethod === "PAYSTACK" && data.data?.authorization_url) {
        window.location.assign(data.data.authorization_url);
        return;
      }
      showToast("Order paid from your wallet", "success");
      await load();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setPaying(false);
    }
  };

  const cancelOrder = async () => {
    if (!window.confirm("Cancel this order? This cannot be undone.")) return;
    setPaying(true);
    try {
      const res = await authFetch(`/marketplace/orders/${id}/cancel/`, {
        method: "POST",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(readApiError(d, "Could not cancel order"));
      }
      showToast("Order cancelled", "success");
      await load();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setPaying(false);
    }
  };

  const toggleItem = (itemId) =>
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  const toggleSelect = (itemId) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  const toggleSelectAll = () => {
    if (selectedIds.size === eligibleItems.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligibleItems.map((r) => r.item.id)));
  };

  if (loading) {
    return (
      <div style={styles.pageWrap}>
        <div style={styles.loadingWrap(colors)}>
          <div style={styles.spinner(colors)} />
          <span style={{ color: colors.secondaryText, fontSize: 13 }}>
            Loading order…
          </span>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={styles.pageWrap}>
        <button
          type="button"
          onClick={() => navigate("/marketplace/orders")}
          style={styles.backBtn(colors)}
        >
          <ChevronLeft size={16} /> My orders
        </button>
        <div style={styles.errorWrap(colors)}>
          <div style={styles.errorIcon(colors)}>
            <AlertCircle size={24} />
          </div>
          <h2 style={styles.errorTitle(colors)}>Order not available</h2>
          <p style={styles.errorText(colors)}>
            {error || "This order may have been removed."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/marketplace/orders")}
            style={styles.primaryBtn(colors)}
          >
            Back to my orders
          </button>
        </div>
      </div>
    );
  }

  const shippingTone = shippingPaymentTone(
    colors,
    order.shipping_payment_status
  );
  const grandTotal = order.grand_total ?? 0;

  return (
    <div style={styles.pageWrap}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={styles.backBtn(colors)}
      >
        <ChevronLeft size={16} /> Back
      </button>

      {/* ── Header ──────────────────────────────────── */}
      <header style={styles.header(colors)}>
        <div style={styles.headerTop}>
          <div style={{ minWidth: 0 }}>
            <div style={styles.headerEyebrow(colors)}>
              Order #{String(order.id).slice(0, 8)}
            </div>
            <div style={styles.headerTitle(colors)}>
              {fmtDate(order.created_at)} · {items.length} item
              {items.length === 1 ? "" : "s"}
            </div>
          </div>
          <span
            style={{
              ...styles.headerStatus,
              background: payment.bg,
              color: payment.fg,
            }}
          >
            {order.payment_status === "COMPLETED" ? (
              <CheckCircle2 size={12} />
            ) : order.payment_status === "FAILED" ? (
              <XCircle size={12} />
            ) : (
              <Clock size={12} />
            )}
            {payment.label}
          </span>
        </div>

        {paid && (
          <div style={styles.headerMetrics}>
            <Metric
              label="Subtotal"
              value={money(order.subtotal)}
              colors={colors}
            />
            <Metric
              label="Shipping"
              value={
                order.shipping_payment_status === "COMPLETED"
                  ? "Paid"
                  : money(order.shipping_fee)
              }
              tone={
                order.shipping_payment_status === "COMPLETED"
                  ? "success"
                  : undefined
              }
              colors={colors}
            />
            <Metric
              label="Total"
              value={money(grandTotal)}
              emphasis
              colors={colors}
            />
          </div>
        )}
      </header>

      {/* ── Unpaid ───────────────────────────────────── */}
      {!paid ? (
        <PaymentGate
          colors={colors}
          order={order}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          onPay={payOrder}
          onCancel={cancelOrder}
          paying={paying}
        />
      ) : (
        <>
          {/* ── Shipping payment block ──────────────── */}
          {eligibleItems.length > 0 && (
            <ShippingPayBlock
              colors={colors}
              eligibleItems={eligibleItems}
              selectedIds={selectedIds}
              onToggle={toggleSelect}
              onToggleAll={toggleSelectAll}
              payableTotal={payableTotal}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              onPay={payShipping}
              paying={paying}
            />
          )}

          {/* ── Shipping fully paid banner ──────────── */}
          {eligibleItems.length === 0 &&
            itemEligibility.some((r) => r.shippingPaid) && (
              <div style={styles.successBanner(colors)}>
                <CheckCircle2 size={16} />
                <div>
                  <div style={{ fontWeight: 700 }}>
                    Shipping paid for all eligible items.
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      marginTop: 2,
                      opacity: 0.85,
                    }}
                  >
                    Your item will be dispatched as soon as the seller
                    processes the release.
                  </div>
                </div>
              </div>
            )}

          {/* ── Fulfilment overview ─────────────────── */}
          <section style={styles.section(colors)}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle(colors)}>Fulfilment</h2>
              <span style={styles.sectionSubtitle(colors)}>
                {order.status_display || statusLabel(order.status)}
              </span>
            </div>
            <OrderLadder order={order} colors={colors} />
          </section>

          {/* ── Tracking ────────────────────────────── */}
          {(order.tracking_number || order.tracking_url) && (
            <section style={styles.section(colors)}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle(colors)}>Tracking</h2>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {order.tracking_number && (
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: colors.heading,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {order.tracking_number}
                  </div>
                )}
                {order.tracking_url && (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: colors.linkColor,
                      textDecoration: "none",
                    }}
                  >
                    Track shipment <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </section>
          )}

          {/* ── Items ───────────────────────────────── */}
          <section style={styles.section(colors)}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle(colors)}>Items</h2>
              <span style={styles.sectionSubtitle(colors)}>
                {items.length} item{items.length === 1 ? "" : "s"}
              </span>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {itemEligibility.map(
                ({ item, eligible, shippingPaid, hasFee, arrived }) => (
                  <BuyerItemCard
                    key={item.id}
                    item={item}
                    colors={colors}
                    eligible={eligible}
                    shippingPaid={shippingPaid}
                    hasFee={hasFee}
                    arrived={arrived}
                    selected={selectedIds.has(item.id)}
                    expanded={expandedItems.has(item.id)}
                    onToggleSelect={() => toggleSelect(item.id)}
                    onToggleExpand={() => toggleItem(item.id)}
                  />
                )
              )}
            </div>
          </section>

          {/* ── Shipping payment status ─────────────── */}
          <div style={styles.shippingStatusRow(colors)}>
            <span style={{ fontSize: 12, color: colors.secondaryText }}>
              Shipping
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11.5,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 999,
                background: shippingTone.bg,
                color: shippingTone.fg,
              }}
            >
              {shippingTone.label}
            </span>
          </div>
        </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Payment gate (unpaid order)
   ══════════════════════════════════════════════════════════ */

const PaymentGate = ({
  colors,
  order,
  paymentMethod,
  setPaymentMethod,
  onPay,
  onCancel,
  paying,
}) => {
  const payment = paymentTone(colors, order.payment_status);
  const isFailed = order.payment_status === "FAILED";
  const isCancelled = order.payment_status === "CANCELLED";

  return (
    <div style={styles.gateCard(colors, payment.fg)}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: payment.bg,
            color: payment.fg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isFailed || isCancelled ? (
            <XCircle size={24} />
          ) : (
            <Clock size={24} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: colors.heading }}>
            {payment.label}
          </div>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13,
              color: colors.secondaryText,
              lineHeight: 1.55,
            }}
          >
            {isFailed
              ? "Your payment didn't go through. Try again or cancel the order."
              : isCancelled
              ? "This order has been cancelled."
              : "Complete payment to start fulfilment. Nothing ships until payment is confirmed."}
          </p>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${colors.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <span
          style={{
            fontSize: 12.5,
            color: colors.secondaryText,
            fontWeight: 600,
          }}
        >
          Order total
        </span>
        <span
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: colors.heading,
            letterSpacing: "-0.02em",
          }}
        >
          {money(order.total)}
        </span>
      </div>

      {!isCancelled && (
        <>
          <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
            {PAYMENT_METHODS.map((m) => {
              const Icon = m.icon;
              const active = paymentMethod === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setPaymentMethod(m.value)}
                  disabled={paying}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    padding: 12,
                    borderRadius: 12,
                    border: `1.5px solid ${
                      active ? colors.btnBg : colors.border
                    }`,
                    background: active ? colors.hoverBg : "transparent",
                    cursor: paying ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 9,
                      background: active ? colors.btnBg : colors.subtleBg,
                      color: active ? colors.btnText : colors.secondaryText,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={15} />
                  </span>
                  <span style={{ flex: 1 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 700,
                        color: colors.heading,
                      }}
                    >
                      {m.title}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 11.5,
                        color: colors.secondaryText,
                        marginTop: 1,
                      }}
                    >
                      {m.subtitle}
                    </span>
                  </span>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: `1.5px solid ${
                        active ? colors.btnBg : colors.border
                      }`,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {active && (
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: colors.btnBg,
                        }}
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={onPay}
              disabled={paying}
              style={{
                padding: "13px 18px",
                borderRadius: 12,
                border: "none",
                background: paying ? colors.subtleBg : colors.btnBg,
                color: paying ? colors.secondaryText : colors.btnText,
                fontSize: 14.5,
                fontWeight: 700,
                cursor: paying ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                boxShadow: paying ? "none" : `0 4px 14px ${colors.btnBg}33`,
                fontFamily: "inherit",
              }}
            >
              {paying ? (
                <>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      border: `2px solid ${colors.btnText}55`,
                      borderTopColor: colors.btnText,
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  Processing…
                </>
              ) : (
                <>
                  Complete payment · {money(order.total)}
                  <ArrowRight size={15} />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={paying}
              style={{
                padding: "13px 18px",
                borderRadius: 12,
                border: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.text,
                fontSize: 13.5,
                fontWeight: 600,
                cursor: paying ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontSize: 11.5,
              color: colors.secondaryText,
            }}
          >
            <ShieldCheck size={12} />
            Secure checkout · Your details are encrypted
          </div>
        </>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Order ladder (compact rollup)
   ══════════════════════════════════════════════════════════ */

const OrderLadder = ({ order, colors }) => {
  const currentIdx = statusIndex(order.status);
  const isTerminal =
    order.status === "FAILED" || order.status === "CANCELLED";
  const pct = isTerminal
    ? 0
    : currentIdx >= 0
    ? ((currentIdx + 1) / STATUS_FLOW.length) * 100
    : 0;
  const next = STATUS_FLOW[currentIdx + 1];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: colors.secondaryText,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.2,
            color: isTerminal ? colors.error : colors.text,
          }}
        >
          {order.status_display || statusLabel(order.status)}
        </span>
        {!isTerminal && currentIdx >= 0 && (
          <span style={{ color: colors.secondaryText }}>
            · step {currentIdx + 1} of {STATUS_FLOW.length}
          </span>
        )}
      </div>
      <div
        style={{
          height: 4,
          borderRadius: 2,
          background: colors.hoverBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: isTerminal ? colors.error : colors.btnBg,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      {!isTerminal && next && (
        <div
          style={{ marginTop: 6, fontSize: 11.5, color: colors.secondaryText }}
        >
          Next: {next.label}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Shipping payment block
   ══════════════════════════════════════════════════════════ */

const ShippingPayBlock = ({
  colors,
  eligibleItems,
  selectedIds,
  onToggle,
  onToggleAll,
  payableTotal,
  paymentMethod,
  setPaymentMethod,
  onPay,
  paying,
}) => {
  const allSelected = selectedIds.size === eligibleItems.length;
  const someSelected = selectedIds.size > 0;

  return (
    <section
      style={{
        borderRadius: 16,
        border: `1px solid ${colors.warning}55`,
        background: colors.raisedBg,
        padding: 18,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 15,
              fontWeight: 800,
              color: colors.heading,
              letterSpacing: "-0.01em",
            }}
          >
            <Truck size={16} color={colors.warning} />
            Shipping fees ready to pay
          </div>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 12.5,
              color: colors.secondaryText,
              lineHeight: 1.5,
            }}
          >
            These items have arrived in Ghana. Pay shipping to have them
            released for pickup or delivery.
          </p>
        </div>
        {eligibleItems.length > 1 && (
          <button
            type="button"
            onClick={onToggleAll}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: colors.linkColor,
              fontSize: 12.5,
              fontWeight: 700,
              padding: "4px 8px",
              borderRadius: 8,
              flexShrink: 0,
              fontFamily: "inherit",
            }}
          >
            {allSelected ? "Clear" : "Select all"}
          </button>
        )}
      </div>

      <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
        {eligibleItems.map(({ item }) => {
          const selected = selectedIds.has(item.id);
          const unit = Number(item.shipping_fee || 0);
          const qty = Number(item.quantity || 1);
          const lineTotal = unit * qty;
          return (
            <div
              key={item.id}
              onClick={() => onToggle(item.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${
                  selected ? colors.btnBg : colors.border
                }`,
                background: selected
                  ? `${colors.btnBg}0A`
                  : colors.raisedBg,
                cursor: "pointer",
                transition:
                  "background 0.12s ease, border-color 0.12s ease",
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 5,
                  border: `1.5px solid ${
                    selected ? colors.btnBg : colors.border
                  }`,
                  background: selected ? colors.btnBg : "transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {selected && <Check size={11} color={colors.btnText} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: colors.heading,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.product_name_display || item.product_name} ×{qty}
                </div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: colors.secondaryText,
                    marginTop: 2,
                  }}
                >
                  {money(unit)}/unit · {money(lineTotal)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gap: 8 }}>
          {PAYMENT_METHODS.map((m) => {
            const Icon = m.icon;
            const active = paymentMethod === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => setPaymentMethod(m.value)}
                disabled={paying}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  padding: 10,
                  borderRadius: 10,
                  border: `1.5px solid ${
                    active ? colors.btnBg : colors.border
                  }`,
                  background: active ? colors.hoverBg : "transparent",
                  cursor: paying ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: active ? colors.btnBg : colors.subtleBg,
                    color: active ? colors.btnText : colors.secondaryText,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={14} />
                </span>
                <span style={{ flex: 1 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: colors.heading,
                    }}
                  >
                    {m.title}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 11,
                      color: colors.secondaryText,
                      marginTop: 1,
                    }}
                  >
                    {m.subtitle}
                  </span>
                </span>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: `1.5px solid ${
                      active ? colors.btnBg : colors.border
                    }`,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {active && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: colors.btnBg,
                      }}
                    />
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          disabled={!someSelected || paying}
          onClick={onPay}
          style={{
            width: "100%",
            padding: "13px 18px",
            borderRadius: 12,
            border: "none",
            background:
              someSelected && !paying
                ? colors.btnBg
                : colors.subtleBg || colors.hoverBg,
            color:
              someSelected && !paying
                ? colors.btnText
                : colors.secondaryText,
            fontSize: 14.5,
            fontWeight: 700,
            cursor: someSelected && !paying ? "pointer" : "not-allowed",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            boxShadow:
              someSelected && !paying
                ? `0 4px 14px ${colors.btnBg}33`
                : "none",
            fontFamily: "inherit",
          }}
        >
          {paying ? (
            <>
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: `2px solid ${colors.btnText}55`,
                  borderTopColor: colors.btnText,
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              Processing…
            </>
          ) : (
            <>
              Pay shipping · {money(payableTotal)}
              <ArrowRight size={15} />
            </>
          )}
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            fontSize: 11.5,
            color: colors.secondaryText,
          }}
        >
          <ShieldCheck size={12} />
          Secure payment · Fees unlock release of your items
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════
   Buyer item card
   ══════════════════════════════════════════════════════════ */

const BuyerItemCard = ({
  item,
  colors,
  eligible,
  shippingPaid,
  hasFee,
  arrived,
  selected,
  expanded,
  onToggleSelect,
  onToggleExpand,
}) => {
  const currentIdx = statusIndex(item.status);
  const next = STATUS_FLOW[currentIdx + 1];
  const pct =
    currentIdx >= 0 ? ((currentIdx + 1) / STATUS_FLOW.length) * 100 : 0;

  return (
    <div
      style={{
        borderRadius: 12,
        border: `1px solid ${
          selected ? colors.btnBg : colors.border
        }`,
        background: selected ? `${colors.btnBg}0A` : colors.raisedBg,
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", gap: 12, padding: 12 }}>
        {eligible && (
          <button
            type="button"
            onClick={onToggleSelect}
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              border: `1.5px solid ${
                selected ? colors.btnBg : colors.border
              }`,
              background: selected ? colors.btnBg : "transparent",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              marginTop: 4,
            }}
          >
            {selected && <Check size={12} color={colors.btnText} />}
          </button>
        )}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 10,
            overflow: "hidden",
            background: colors.subtleBg,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {item.product_image ? (
            <img
              src={item.product_image}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Package size={20} color={colors.secondaryText} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.heading,
                  lineHeight: 1.35,
                }}
              >
                {item.product_name_display || item.product_name}
              </div>
              {item.variant_key && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginTop: 4,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: `${colors.btnBg}18`,
                    color: colors.linkColor,
                    fontSize: 10.5,
                    fontWeight: 700,
                  }}
                >
                  {variantKeyLabel(item.variant_key)}
                </div>
              )}
              <div
                style={{
                  fontSize: 12,
                  color: colors.secondaryText,
                  marginTop: 4,
                }}
              >
                Qty {item.quantity} · {money(item.price)} each ·{" "}
                {money(item.total)}
              </div>
            </div>
            <button
              type="button"
              onClick={onToggleExpand}
              aria-label={expanded ? "Collapse" : "Expand"}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.secondaryText,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {expanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </button>
          </div>

          <div style={{ marginTop: 10 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                color:
                  currentIdx >= STATUS_FLOW.length - 1
                    ? colors.success
                    : colors.text,
              }}
            >
              {item.status_display || statusLabel(item.status)}
              {currentIdx >= 0 &&
                currentIdx < STATUS_FLOW.length - 1 && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontWeight: 600,
                      color: colors.secondaryText,
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    · {currentIdx + 1}/{STATUS_FLOW.length}
                  </span>
                )}
            </div>
            <div
              style={{
                marginTop: 5,
                height: 3,
                borderRadius: 2,
                background: colors.hoverBg,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: colors.btnBg,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            {next && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: colors.secondaryText,
                }}
              >
                Next: {next.label}
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              flexWrap: "wrap",
              paddingTop: 8,
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: colors.secondaryText,
                fontWeight: 600,
              }}
            >
              {hasFee
                ? `Shipping ${money(item.shipping_fee)}/unit · ${money(
                    item.shipping_total
                  )}`
                : arrived
                ? "Shipping fee pending"
                : "Shipping not yet set"}
            </span>
            {shippingPaid && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.success,
                }}
              >
                <CheckCircle2 size={12} />
                Shipping paid
              </span>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div
          style={{
            padding: "0 16px 14px",
            borderTop: `1px solid ${colors.border}`,
            paddingTop: 12,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              color: colors.secondaryText,
              marginBottom: 8,
            }}
          >
            Progress
          </div>
          <ol
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
              display: "grid",
              gap: 2,
            }}
          >
            {STATUS_FLOW.map((s, i) => {
              const passed = i < currentIdx;
              const current = i === currentIdx;
              return (
                <li
                  key={s.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 0",
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 7,
                      border: `1.5px solid ${
                        current
                          ? colors.btnBg
                          : passed
                          ? colors.btnBg
                          : colors.border
                      }`,
                      background:
                        passed || current ? colors.btnBg : "transparent",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {passed && <Check size={9} color={colors.btnText} />}
                    {current && (
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: 3,
                          background: colors.btnText,
                        }}
                      />
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: current ? 800 : passed ? 600 : 500,
                      color: current
                        ? colors.heading
                        : passed
                        ? colors.text
                        : colors.secondaryText,
                    }}
                  >
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Metric
   ══════════════════════════════════════════════════════════ */

const Metric = ({ label, value, tone, emphasis, colors }) => {
  const color =
    tone === "success"
      ? colors.success
      : emphasis
      ? colors.heading
      : colors.text;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          color: colors.secondaryText,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: emphasis ? 18 : 14,
          fontWeight: emphasis ? 800 : 700,
          color,
          letterSpacing: emphasis ? "-0.01em" : 0,
        }}
      >
        {value}
      </span>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "16px 16px 60px",
  },
  backBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "transparent",
    border: "none",
    color: c.secondaryText,
    cursor: "pointer",
    padding: "6px 0",
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 600,
  }),

  header: (c) => ({
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
  }),
  headerTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  headerEyebrow: (c) => ({
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: c.secondaryText,
  }),
  headerTitle: (c) => ({
    marginTop: 4,
    fontSize: 15,
    fontWeight: 700,
    color: c.heading,
    letterSpacing: "-0.01em",
  }),
  headerStatus: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 12px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    flexShrink: 0,
  },
  headerMetrics: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    paddingTop: 14,
    borderTop: "1px solid transparent",
  },

  gateCard: (c, fg) => ({
    padding: 20,
    borderRadius: 16,
    border: `1px solid ${fg}55`,
    background: c.raisedBg,
    marginBottom: 16,
  }),

  successBanner: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 12,
    background: c.successBg,
    color: c.success,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 16,
  }),

  section: (c) => ({
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  }),
  sectionHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  sectionTitle: (c) => ({
    margin: 0,
    fontSize: 15,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.01em",
  }),
  sectionSubtitle: (c) => ({
    fontSize: 12.5,
    color: c.secondaryText,
    fontWeight: 600,
  }),

  shippingStatusRow: (c) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "12px 14px",
    borderRadius: 12,
    background: c.subtleBg || c.hoverBg,
    border: `1px solid ${c.border}`,
    marginTop: 8,
  }),

  loadingWrap: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 320,
    gap: 12,
  }),
  spinner: (c) => ({
    width: 32,
    height: 32,
    border: `3px solid ${c.border}`,
    borderTopColor: c.btnBg,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  }),
  errorWrap: (c) => ({
    maxWidth: 380,
    margin: "60px auto",
    textAlign: "center",
    padding: 24,
  }),
  errorIcon: (c) => ({
    width: 64,
    height: 64,
    borderRadius: 20,
    background: c.hoverBg,
    color: c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  }),
  errorTitle: (c) => ({
    margin: "0 0 6px",
    fontSize: 18,
    fontWeight: 800,
    color: c.heading,
  }),
  errorText: (c) => ({
    margin: "0 0 22px",
    fontSize: 13.5,
    color: c.secondaryText,
    lineHeight: 1.55,
  }),
  primaryBtn: (c) => ({
    padding: "11px 20px",
    borderRadius: 10,
    border: "none",
    background: c.btnBg,
    color: c.btnText,
    fontWeight: 700,
    fontSize: 13.5,
    cursor: "pointer",
  }),
};